import { Link } from "react-router-dom";
import { LOGO } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowRight, Sparkles, Camera, LineChart, BookOpen, Play } from "lucide-react";
import { useI18n } from "../lib/LanguageContext";

const HERO_IMG = "https://images.pexels.com/photos/36088277/pexels-photo-36088277.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LAPTOP_IMG = "https://images.pexels.com/photos/6372896/pexels-photo-6372896.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const VEG_IMG = "https://images.pexels.com/photos/5418661/pexels-photo-5418661.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const { t } = useI18n();
  return (
    <div className="hero-bg" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="wheat-accent right-[-80px] top-[80px]"/>
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative z-10">
            <div className="inline-flex items-center gap-2 bg-teal-soft border border-teal/20 text-teal px-4 py-1.5 rounded-full text-xs tracking-[0.15em] uppercase font-bold mb-6">
              <Sparkles className="w-3.5 h-3.5"/> {t("hero.badge")}
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] text-foreground">
              {t("hero.title1")} <span className="text-teal">{t("hero.title_gizi")}</span> {t("hero.title2")} <span className="text-lime-brand">{t("hero.title_semua")}</span>.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/nuna"><Button className="rounded-full bg-teal hover:bg-teal/90 text-white h-12 px-7 text-base" data-testid="hero-nuna-btn">{t("hero.cta_nuna")} <ArrowRight className="ml-2 w-4 h-4"/></Button></Link>
              <Link to="/edukasi"><Button variant="outline" className="rounded-full h-12 px-7 border-teal/30 text-teal hover:bg-teal-soft" data-testid="hero-edu-btn">{t("hero.cta_edu")}</Button></Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-muted-foreground">
              <div><span className="text-2xl font-display font-medium text-foreground">5+</span><div>{t("hero.stat_sources")}</div></div>
              <div className="w-px h-10 bg-teal/20"/>
              <div><span className="text-2xl font-display font-medium text-foreground">AI</span><div>{t("hero.stat_media")}</div></div>
              <div className="w-px h-10 bg-teal/20"/>
              <div><span className="text-2xl font-display font-medium text-foreground">7×</span><div>{t("hero.stat_trial")}</div></div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="bento-tile shadow-[0_30px_80px_-20px_rgba(47,163,159,0.35)]">
              <img src={HERO_IMG} alt="nutrition" className="w-full h-[520px] object-cover"/>
            </div>
            <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 border border-teal/15 w-64 shadow-lg">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-2">{t("landing.meal")}</div>
              <div className="text-2xl font-display font-medium">742 kcal</div>
              <div className="nutrient-bar mt-2"><span style={{width:"68%"}}/></div>
              <div className="text-xs text-muted-foreground mt-2">68% {t("landing.daily")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE LOGOS */}
      <section className="py-8 border-y border-teal/10 bg-white overflow-hidden">
        <div className="marquee">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-14 px-8 shrink-0">
              {["TKPI Indonesia","Nutrisurvey","Thai FCT","Malaysian FCT","Korean Nutrition Society DB","USDA FoodData Central","AKG 2019"].map((s) => (
                <span key={s} className="text-sm tracking-[0.18em] uppercase font-bold text-muted-foreground/70">{s}</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-3xl mb-16">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-3">{t("landing.pillars")}</div>
          <h2 className="font-display text-4xl md:text-5xl font-medium">{t("landing.tagline")}</h2>
        </div>
        <div className="grid md:grid-cols-12 gap-6">
          <FeatureTile className="md:col-span-8 min-h-[280px] bg-gradient-to-br from-teal/95 to-teal text-white" title={t("features.nuna_title")} desc={t("features.nuna_desc")} icon={<LineChart className="w-6 h-6"/>} cta={t("features.nuna_cta")} to="/nuna"/>
          <FeatureTile className="md:col-span-4 bg-white" title={t("features.edu_title")} desc={t("features.edu_desc")} icon={<BookOpen className="w-5 h-5"/>} cta={t("features.edu_cta")} to="/edukasi"/>
          <FeatureTile className="md:col-span-4 bg-lime-brand/10" title={t("features.shop_title")} desc={t("features.shop_desc")} icon={<BookOpen className="w-5 h-5"/>} cta={t("features.shop_cta")} to="/produk"/>
          <FeatureTile className="md:col-span-8 bg-white overflow-hidden relative" title={t("features.ai_title")} desc={t("features.ai_desc")} icon={<Camera className="w-5 h-5"/>} cta={t("features.ai_cta")} to="/nuna" image={LAPTOP_IMG}/>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-teal-soft/40 py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-3">{t("landing.workflow")}</div>
            <h2 className="font-display text-4xl md:text-5xl font-medium mb-8">{t("landing.steps_title")}</h2>
            <ol className="space-y-6">
              {[
                {n:"01", t:t("landing.step1"), d:t("landing.step1_desc")},
                {n:"02", t:t("landing.step2"), d:t("landing.step2_desc")},
                {n:"03", t:t("landing.step3"), d:t("landing.step3_desc")},
              ].map((s) => (
                <li key={s.n} className="flex gap-5">
                  <div className="font-display text-3xl text-lime-brand font-medium">{s.n}</div>
                  <div>
                    <h3 className="font-display text-xl font-medium">{s.t}</h3>
                    <p className="text-muted-foreground mt-1">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="relative">
            <img src={VEG_IMG} alt="fresh food" className="rounded-3xl w-full h-[520px] object-cover"/>
            <div className="absolute top-6 right-6 glass rounded-2xl px-5 py-4 border border-teal/15">
              <div className="text-xs tracking-[0.18em] uppercase text-teal font-bold">Live</div>
              <div className="text-lg font-display font-medium mt-1">{t("landing.match")}</div>
              <div className="text-3xl font-display text-teal">92<span className="text-lg text-muted-foreground">/100</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24 text-center">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-3">{t("landing.start")}</div>
        <h2 className="font-display text-4xl md:text-6xl font-medium mb-6">{t("landing.cta_title")}</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto mb-8">{t("landing.cta_desc")}</p>
        <Link to="/daftar"><Button className="rounded-full bg-teal hover:bg-teal/90 text-white h-12 px-8" data-testid="cta-signup-btn">{t("landing.cta_button")} <ArrowRight className="ml-2 w-4 h-4"/></Button></Link>
      </section>
    </div>
  );
}

function FeatureTile({ className = "", title, desc, icon, cta, to, image }) {
  return (
    <Link to={to} className={`bento-tile p-7 flex flex-col justify-between group relative ${className}`}>
      {image && <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity"/>}
      <div className="relative">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/20 mb-4">{icon}</div>
        <h3 className="font-display text-2xl font-medium leading-tight">{title}</h3>
        <p className="mt-2 opacity-80 text-sm max-w-md">{desc}</p>
      </div>
      <div className="relative mt-4 inline-flex items-center text-sm font-semibold">{cta} <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1"/></div>
    </Link>
  );
}
